import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Button, showSnackbar, TabPage, TabView } from 'fluxo-ui';
import { BarChartIcon, EditIcon, EyeIcon, ListIcon } from 'fluxo-ui/icons';

import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import { Analytics, GradingInterface, IndividualResult, ResultsDashboard } from '../components/results';

const MANUAL_GRADING_QUESTION_TYPES = new Set(['short_answer', 'long_answer', 'code', 'matching', 'ordering']);

const resolveMaxMarks = (ans: any, question: any, exam: any): number => {
    if (typeof ans.maxMarks === 'number' && ans.maxMarks > 0) return ans.maxMarks;
    const questionIdStr = String(question?._id || ans.questionId);
    const examQuestion = exam?.questions?.find((q: any) => String((q.questionId as any)?._id || q.questionId) === questionIdStr);
    return examQuestion?.marks || question?.defaultMarks || 1;
};

const resolveStudentAnswerText = (ans: any, question: any): string => {
    if (ans.answer === undefined || ans.answer === null) return '';
    if (!Array.isArray(question?.options) || question.options.length === 0) {
        return typeof ans.answer === 'string' ? ans.answer : JSON.stringify(ans.answer);
    }
    const optionText = (optionId: string) => question.options.find((opt: any) => opt.id === optionId)?.text || optionId;
    if (Array.isArray(ans.answer)) {
        return ans.answer.map(optionText).join(', ');
    }
    if (typeof ans.answer === 'string') {
        return optionText(ans.answer);
    }
    return JSON.stringify(ans.answer);
};
import analyticsService from '../services/analytics.service';
import attemptService from '../services/attempt.service';
import examService from '../services/exam.service';

const mapAttempt = (a: any, examData: any) => ({
    id: a._id,
    attemptId: a.attemptId || a._id,
    userId: a.userId?._id || a.userId || '',
    userName: a.userId?.profile?.name || a.userId?.name || a.userId?.email || 'Unknown',
    userEmail: a.userId?.email || '',
    attemptNumber: a.attemptNumber || 1,
    startTime: a.startTime || a.createdAt,
    endTime: a.endTime,
    status: a.status,
    scoredMarks: a.scoredMarks || 0,
    totalMarks: a.totalMarks || examData?.totalMarks || 0,
    percentage: a.percentage || 0,
    passed: a.passed || false,
    gradingStatus: a.gradingStatus || 'auto_graded',
    timeElapsedSeconds: a.timeElapsedSeconds || 0,
    examId: String((a.examId as any)?._id || a.examId || ''),
    examTitle: (a.examId as any)?.title || '',
});

const ResultsPage: React.FC = () => {
    const { attemptId, examId: examIdParam } = useParams<{ attemptId: string; examId: string }>();
    const navigate = useNavigate();
    const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Data state
    const [attempt, setAttempt] = useState<any>(null);
    const [attemptAnswers, setAttemptAnswers] = useState<any[]>([]);
    const [exam, setExam] = useState<any>(null);
    const [attempts, setAttempts] = useState<any[]>([]);
    const [myAttempts, setMyAttempts] = useState<any[]>([]);
    const [examAnalytics, setExamAnalytics] = useState<any>(null);
    const [selectedAttemptDetail, setSelectedAttemptDetail] = useState<any>(null);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attemptId, examIdParam]);

    const loadExamScope = async (examId: string, examDataPreloaded?: any) => {
        const [examData, examAttempts] = await Promise.all([
            examDataPreloaded ? Promise.resolve(examDataPreloaded) : examService.getExam(examId).catch(() => null),
            attemptService.getExamAttempts(examId).catch(() => attemptService.getUserAttempts(examId).catch(() => [])),
        ]);
        setExam(examData);
        setAttempts((Array.isArray(examAttempts) ? examAttempts : []).map((a: any) => mapAttempt(a, examData)));
        try {
            const analytics = await analyticsService.getExamAnalytics(examId);
            setExamAnalytics(analytics);
        } catch {
            // Analytics may not be available for all exams
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            if (attemptId) {
                // Backend responds { attempt, answers }; examId may be populated
                const { attempt: attemptData, answers: answersData } = await attemptService.getAttempt(attemptId);
                setAttempt(attemptData);
                setAttemptAnswers(answersData || []);
                await loadExamScope(String((attemptData.examId as any)?._id || attemptData.examId));
            } else if (examIdParam) {
                await loadExamScope(examIdParam);
            } else {
                const userAttempts = await attemptService.getUserAttempts().catch(() => []);
                setMyAttempts((Array.isArray(userAttempts) ? userAttempts : []).map((a: any) => mapAttempt(a, null)));
            }
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load results', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleViewResult = async (id: string) => {
        try {
            const result = await attemptService.getAttemptResults(id);
            setSelectedAttemptDetail(result);
            setSelectedAttemptId(id);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load result details', 'Error', { type: 'error' });
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
                    <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded"></div>
                </div>
            </div>
        );
    }

    if (!attemptId && !examIdParam) {
        return (
            <div className="space-y-6">
                <PageHeader title="My Results" description="Your exam attempts and scores" className="mb-0" />
                {myAttempts.length === 0 ? (
                    <Card>
                        <EmptyState
                            icon={BarChartIcon}
                            title="No attempts yet"
                            description="Once you take an exam, your results will appear here."
                            action={<Button label="Browse Exams" variant="primary" onClick={() => navigate('/exams')} />}
                        />
                    </Card>
                ) : (
                    <Card padding="none">
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {myAttempts.map((a) => (
                                <button
                                    key={a.id}
                                    onClick={() => navigate(`/results/${a.id}`)}
                                    className="w-full flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                                            {a.examTitle || 'Exam attempt'}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            Attempt #{a.attemptNumber} • {a.startTime ? new Date(a.startTime).toLocaleString() : '—'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Badge tone={a.status === 'in_progress' ? 'warning' : a.passed ? 'success' : 'danger'} dot>
                                            {a.status === 'in_progress' ? 'In progress' : a.passed ? 'Passed' : 'Failed'}
                                        </Badge>
                                        <span className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                                            {Math.round((a.percentage || 0) * 10) / 10}%
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Card>
                )}
            </div>
        );
    }

    if (!attempt && !exam) {
        return (
            <div className="p-6 text-center">
                <p className="text-slate-500 dark:text-slate-400">No results found.</p>
            </div>
        );
    }

    const examTitle = exam?.title || (attempt?.examId as any)?.title || 'Exam Results';
    const totalMarks = attempt?.totalMarks || exam?.totalMarks || 100;

    // Score distribution computed from the loaded attempts (works even when
    // the analytics endpoint is unavailable)
    const scoreDistribution = attempts.length
        ? [
              {
                  range: '90-100%',
                  count: attempts.filter((a) => a.percentage >= 90).length,
                  percentage: attempts.length ? (attempts.filter((a) => a.percentage >= 90).length / attempts.length) * 100 : 0,
              },
              {
                  range: '80-89%',
                  count: attempts.filter((a) => a.percentage >= 80 && a.percentage < 90).length,
                  percentage: attempts.length
                      ? (attempts.filter((a) => a.percentage >= 80 && a.percentage < 90).length / attempts.length) * 100
                      : 0,
              },
              {
                  range: '70-79%',
                  count: attempts.filter((a) => a.percentage >= 70 && a.percentage < 80).length,
                  percentage: attempts.length
                      ? (attempts.filter((a) => a.percentage >= 70 && a.percentage < 80).length / attempts.length) * 100
                      : 0,
              },
              {
                  range: '60-69%',
                  count: attempts.filter((a) => a.percentage >= 60 && a.percentage < 70).length,
                  percentage: attempts.length
                      ? (attempts.filter((a) => a.percentage >= 60 && a.percentage < 70).length / attempts.length) * 100
                      : 0,
              },
              {
                  range: '0-59%',
                  count: attempts.filter((a) => a.percentage < 60).length,
                  percentage: attempts.length ? (attempts.filter((a) => a.percentage < 60).length / attempts.length) * 100 : 0,
              },
          ]
        : [];

    const avgScore = attempts.length ? attempts.reduce((sum, a) => sum + a.percentage, 0) / attempts.length : 0;
    const passRate = attempts.length ? (attempts.filter((a) => a.passed).length / attempts.length) * 100 : 0;
    const avgTime = attempts.length ? attempts.reduce((sum, a) => sum + a.timeElapsedSeconds, 0) / attempts.length : 0;

    return (
        <div className="p-6">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{examTitle}</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">View results, analytics, and grading</p>
                </div>
                {exam?._id && (
                    <Button
                        label="Export CSV"
                        variant="default"
                        layout="outlined"
                        onClick={async () => {
                            try {
                                await examService.exportResultsCSV(exam._id, examTitle);
                                showSnackbar('Results exported', 'Download started', { type: 'success' });
                            } catch (error: any) {
                                showSnackbar(error.message || 'Failed to export results', 'Error', { type: 'error' });
                            }
                        }}
                    />
                )}
            </div>

            <TabView className="border border-slate-200 dark:border-slate-700 rounded-lg">
                <TabPage header="Overview" leftIcon={BarChartIcon}>
                    <div className="p-6">
                        <ResultsDashboard
                            examId={exam?._id || attempt?.examId}
                            examTitle={examTitle}
                            totalMarks={totalMarks}
                            attempts={attempts}
                            loading={loading}
                            onViewResult={handleViewResult}
                        />
                    </div>
                </TabPage>

                <TabPage header="Analytics" leftIcon={ListIcon}>
                    <div className="p-6">
                        <Analytics
                            examTitle={examTitle}
                            totalAttempts={attempts.length}
                            avgScore={Math.round(avgScore)}
                            passRate={Math.round(passRate)}
                            avgTimeSeconds={Math.round(avgTime)}
                            scoreDistribution={scoreDistribution}
                            questions={
                                examAnalytics?.questionPerformance?.map((q: any, idx: number) => ({
                                    questionId: String(q.questionId),
                                    questionNumber: idx + 1,
                                    questionTitle: q.questionTitle || `Question ${idx + 1}`,
                                    questionType: q.questionType || 'unknown',
                                    maxMarks: q.maxMarks || 1,
                                    attempts: q.totalAttempts || 0,
                                    avgScore: q.averageScore || 0,
                                    avgTimeSeconds: q.averageTime || 0,
                                    correctCount: q.correctCount || 0,
                                    partialCount: q.partialCount || 0,
                                    incorrectCount: Math.max(0, (q.totalAttempts || 0) - (q.correctCount || 0) - (q.partialCount || 0)),
                                    difficultyIndex: q.difficultyIndex || 0,
                                    discriminationIndex: q.discriminationIndex || 0,
                                })) || []
                            }
                        />
                    </div>
                </TabPage>

                <TabPage header="Grading" leftIcon={EditIcon}>
                    <div className="p-6">
                        {(() => {
                            const manualAnswers = attemptAnswers.filter((ans: any) => {
                                const question = typeof ans.questionId === 'object' ? ans.questionId : {};
                                return MANUAL_GRADING_QUESTION_TYPES.has(question.type);
                            });

                            return attempt && manualAnswers.length > 0 ? (
                                <GradingInterface
                                    attempt={{
                                        attemptId: attempt.attemptId || attempt._id,
                                        studentName: (attempt.userId as any)?.profile?.name || (attempt.userId as any)?.name || 'Student',
                                        studentEmail: (attempt.userId as any)?.email || '',
                                        examTitle: examTitle,
                                        // Keyed by the ANSWER id so grades can be saved per answer
                                        questions: manualAnswers.map((ans: any, idx: number) => {
                                            const question = typeof ans.questionId === 'object' ? ans.questionId : {};
                                            return {
                                                questionId: String(ans._id),
                                                questionNumber: idx + 1,
                                                questionTitle: question.title || question.text || `Question ${idx + 1}`,
                                                questionType: question.type || 'short_answer',
                                                questionContent: question.text || '',
                                                maxMarks: resolveMaxMarks(ans, question, exam),
                                                correctAnswer:
                                                    typeof question.correctAnswer === 'string'
                                                        ? question.correctAnswer
                                                        : question.correctAnswer !== undefined
                                                          ? JSON.stringify(question.correctAnswer)
                                                          : undefined,
                                                studentAnswer: resolveStudentAnswerText(ans, question),
                                                marksAwarded: ans.marksAwarded,
                                                feedback: ans.gradingComments,
                                                gradingStatus: ans.gradedAt ? 'graded' : 'pending',
                                            };
                                        }),
                                    }}
                                    onSave={async (grades) => {
                                        for (const grade of grades) {
                                            await attemptService.gradeAnswer(
                                                attempt._id,
                                                grade.questionId,
                                                grade.marksAwarded,
                                                grade.feedback,
                                            );
                                        }
                                        await loadData();
                                    }}
                                />
                            ) : (
                                <p className="text-slate-500 dark:text-slate-400">No answers require manual grading for this attempt.</p>
                            );
                        })()}
                    </div>
                </TabPage>

                {selectedAttemptId && selectedAttemptDetail && (
                    <TabPage header="Individual Result" leftIcon={EyeIcon}>
                        <div className="p-6">
                            {(() => {
                                // getAttemptResults responds { attempt, answers, gradingSummary }
                                const detailAttempt = selectedAttemptDetail.attempt || {};
                                const detailAnswers = selectedAttemptDetail.answers || [];
                                return (
                                    <IndividualResult
                                        attempt={{
                                            id: detailAttempt._id || selectedAttemptId,
                                            attemptId: detailAttempt.attemptId || selectedAttemptId,
                                            examTitle: examTitle,
                                            studentName:
                                                (detailAttempt.userId as any)?.profile?.name ||
                                                (detailAttempt.userId as any)?.name ||
                                                'Student',
                                            studentEmail: (detailAttempt.userId as any)?.email || '',
                                            attemptNumber: detailAttempt.attemptNumber || 1,
                                            startTime: detailAttempt.startTime,
                                            endTime: detailAttempt.endTime || '',
                                            timeElapsedSeconds: detailAttempt.timeElapsedSeconds || 0,
                                            status: detailAttempt.status,
                                            scoredMarks: detailAttempt.scoredMarks || 0,
                                            totalMarks: detailAttempt.totalMarks || totalMarks,
                                            percentage: detailAttempt.percentage || 0,
                                            passed: detailAttempt.passed || false,
                                            gradingStatus: detailAttempt.gradingStatus || 'auto_graded',
                                        }}
                                        answers={detailAnswers.map((ans: any, idx: number) => {
                                            const question = typeof ans.questionId === 'object' ? ans.questionId : {};
                                            return {
                                                questionId: String(question._id || ans.questionId),
                                                questionNumber: idx + 1,
                                                questionTitle: question.title || question.text || `Question ${idx + 1}`,
                                                questionType: question.type || 'unknown',
                                                questionContent: question.text || '',
                                                correctAnswer:
                                                    typeof question.correctAnswer === 'string'
                                                        ? question.correctAnswer
                                                        : question.correctAnswer !== undefined
                                                          ? JSON.stringify(question.correctAnswer)
                                                          : undefined,
                                                studentAnswer: resolveStudentAnswerText(ans, question),
                                                isCorrect: ans.isCorrect,
                                                marksAwarded: ans.marksAwarded || 0,
                                                maxMarks: resolveMaxMarks(ans, question, exam),
                                                timeSpentSeconds: ans.timeSpentSeconds || 0,
                                                gradingMethod: ans.gradingMethod || 'auto',
                                                feedback: ans.gradingComments,
                                            };
                                        })}
                                    />
                                );
                            })()}
                        </div>
                    </TabPage>
                )}
            </TabView>
        </div>
    );
};

export default ResultsPage;
